import React, { Suspense, lazy, useEffect } from "react";
import { AdminTwoFactorGate } from './components/admin/AdminTwoFactorGate';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HealthProvider } from "./context/HealthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { syncSettingsFromDatabase } from "./utils/getSiteSettings";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent";
import GoogleAnalytics from "./components/GoogleAnalytics";
import ScrollToTop from "./components/ScrollToTop";

// Eager-load landing page (first paint, critical for LCP)
import LandingPage from "./pages/LandingPage";

// Lazy-load everything else (code splitting — reduces initial JS bundle)
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const HealthHubPage = lazy(() => import("./pages/HealthHubPage"));
const HealthArticlePage = lazy(() => import("./pages/HealthArticlePage"));
const CompactKeywordPage = lazy(() => import("./pages/CompactKeywordPage"));

// Minimal loading fallback (no layout shift — just a subtle fade-in)
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: 32, height: 32, border: '3px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
  </div>
);

const PrivacyGuardNudge = React.lazy(() => import('./components/PrivacyGuardNudge'));

function App() {
  // Sync settings from database on app load
  useEffect(() => {
    syncSettingsFromDatabase().catch(console.error);
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <ThemeProvider>
            <HealthProvider>
            <Suspense fallback={null}><PrivacyGuardNudge /></Suspense>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/discover" element={<MarketingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ForgotPasswordPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/profile" element={<UserProfilePage />} />
                <Route path="/admin" element={<AdminTwoFactorGate><AdminDashboard /></AdminTwoFactorGate>} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment/success" element={<PaymentSuccessPage />} />
                <Route path="/payment/cancel" element={<PaymentCancelPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/health" element={<HealthHubPage />} />
                <Route path="/health/:slug" element={<HealthArticlePage />} />
                <Route path="/solutions/:slug" element={<CompactKeywordPage />} />

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>

            {/* Cookie Consent Banner */}
            <CookieConsent />
            <GoogleAnalytics />

            {/* REMOVED: SessionExpiryModal - broke mobile (Jan 28, 2026) */}
          </HealthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
